import { useState, useRef, useEffect } from 'react';
import { format, addSeconds } from 'date-fns';
import api from '../../api/client';

function fmtTime(d) {
  return d ? format(d, 'HH:mm:ss') : '—';
}

function fmtDur(s) {
  if (!s || s <= 0) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
}

export default function Timeline({ stats }) {
  const playback = stats?.playback;
  const queue = stats?.queue || [];
  const history = stats?.queue_history || [];
  const [busy, setBusy] = useState(null);
  const [dragId, setDragId] = useState(null);
  const scrollRef = useRef(null);
  const currentRef = useRef(null);
  const prevTrackKey = useRef(null);

  const now = new Date();
  const current = playback?.currentTrack || null;
  const remaining = playback?.remaining || 0;
  const currentKey = current ? `${current.title}|${current.media_file_id}` : 'none';

  // Mantener el tema actual siempre arriba al cargar o cuando cambia
  useEffect(() => {
    if (prevTrackKey.current !== currentKey) {
      prevTrackKey.current = currentKey;
      const container = scrollRef.current;
      const currentEl = currentRef.current;
      if (container && currentEl) {
        requestAnimationFrame(() => {
          container.scrollTop = currentEl.offsetTop - container.offsetTop;
        });
      }
    }
  }, [currentKey]);

  // Estimación de inicio de cada próximo tema
  let cursor = addSeconds(now, remaining);
  const upcoming = queue.map((item) => {
    const itemTime = new Date(cursor);
    cursor = addSeconds(cursor, item.duration_seconds || 0);
    return { item, time: itemTime };
  });

  // Historial: history[0] es el más reciente; se muestran de más antiguo (arriba) a más reciente (abajo, pegado al actual)
  let pastCursor = new Date(now);
  const recent = history.map((item) => {
    const itemTime = new Date(pastCursor);
    pastCursor = new Date(pastCursor.getTime() - (item.duration_seconds || 0) * 1000);
    return { item, time: itemTime };
  }).reverse();

  const send = async (action, body) => {
    setBusy(action + (body?.media_file_id || ''));
    try {
      await api.post(`/api/playback/${action}`, body || {});
    } catch (err) {
      console.error(`[Timeline] ${action} failed:`, err);
    } finally {
      setBusy(null);
    }
  };

  // Reordenar la cola: envía el orden completo de ids al engine
  const handleReorderTo = async (sourceId, targetIdx) => {
    if (!sourceId || targetIdx < 0 || targetIdx >= upcoming.length) return;
    const list = upcoming.map(({ item }) => String(item.media_file_id || item.id));
    const from = list.indexOf(String(sourceId));
    if (from === -1 || from === targetIdx) return;
    const [moved] = list.splice(from, 1);
    list.splice(targetIdx, 0, moved);
    try {
      await api.post('/api/playback/reorder', { ids: list });
    } catch (err) {
      console.error('[Timeline] reorder failed:', err);
    }
  };

  const moveBy = async (idx, delta) => {
    const to = idx + delta;
    if (to < 0 || to >= upcoming.length) return;
    const item = upcoming[idx].item;
    if (!item.media_file_id) return;
    await handleReorderTo(String(item.media_file_id), to);
  };

  return (
    <div className="rounded-xl bg-dark-850 border border-dark-700 p-4">
      <h2 className="text-base font-semibold text-dark-100 mb-2">📻 Línea de tiempo de la radio</h2>

      <div ref={scrollRef} className="max-h-[340px] overflow-y-auto rounded-lg border border-dark-700 bg-dark-900/40">
        <div className="space-y-1 p-2">
          {/* Historial (subir para ver lo que ya pasó) */}
          {recent.length > 0 && (
            <p className="text-[10px] font-medium uppercase tracking-wide text-dark-500 px-2 pt-1">
              ↑ Sonó recientemente
            </p>
          )}
          {recent.map(({ item, time }, i) => (
            <div key={`r-${i}`} className="flex items-center gap-2 rounded px-2 py-1.5 bg-dark-800/30">
              <span className="text-xs text-dark-600">✔</span>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-dark-500 truncate">{item.title || '—'}</p>
                <p className="text-[10px] text-dark-600 truncate">{item.artist || ''}{item.program ? ` · ${item.program}` : ''}</p>
              </div>
              <span className="text-[10px] font-mono text-dark-600">{fmtTime(time)}</span>
            </div>
          ))}

          {/* Actual (siempre visible) */}
          {current ? (
            <div ref={currentRef} className="sticky top-0 z-10 bg-dark-850 border border-primary-700/60 rounded-lg px-3 py-2 my-1">
              <div className="flex items-center gap-2">
                <span className="text-base">🔴</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-primary-300 truncate">{current.title || '—'}</p>
                  <p className="text-xs text-dark-300 truncate">{current.artist || ''}{current.program ? ` · ${current.program}` : ''}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => send('seek', { delta: -15 })}
                    disabled={busy === 'seek-15'}
                    className="flex h-7 w-7 items-center justify-center rounded bg-dark-700 text-dark-200 hover:bg-dark-600 text-xs"
                    title="Atrasar 15 segundos"
                  >
                    ⏪
                  </button>
                  <button
                    type="button"
                    onClick={() => send('seek', { delta: 15 })}
                    disabled={busy === 'seek15'}
                    className="flex h-7 w-7 items-center justify-center rounded bg-dark-700 text-dark-200 hover:bg-dark-600 text-xs"
                    title="Adelantar 15 segundos"
                  >
                    ⏩
                  </button>
                  <button
                    type="button"
                    onClick={() => send('back')}
                    disabled={busy === 'back'}
                    className="flex h-8 w-8 items-center justify-center rounded bg-dark-700 text-dark-200 hover:bg-dark-600"
                    title="Volver al anterior"
                  >
                    ⏮
                  </button>
                  <button
                    type="button"
                    onClick={() => send('skip')}
                    disabled={busy === 'skip'}
                    className="flex h-8 w-8 items-center justify-center rounded bg-primary-600 text-white hover:bg-primary-700"
                    title="Saltar al siguiente"
                  >
                    ⏭
                  </button>
                </div>
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <div className="h-1.5 flex-1 rounded-full bg-dark-700 overflow-hidden">
                  <div
                    className="h-full rounded-full bg-primary-500"
                    style={{
                      width: current.duration_seconds
                        ? `${Math.min(100, Math.max(0, (1 - remaining / current.duration_seconds) * 100))}%`
                        : '0%',
                    }}
                  />
                </div>
                <span className="text-[10px] font-mono text-dark-400">{Math.round(remaining)}s</span>
              </div>
            </div>
          ) : (
            <div className="sticky top-0 z-10 bg-dark-850 border border-dark-700 rounded-lg px-3 py-2 my-1">
              <p className="text-sm text-dark-400">No hay reproducción en este momento.</p>
            </div>
          )}

          {/* Próximos (bajar para ver más) */}
          {upcoming.length > 0 && (
            <p className="text-[10px] font-medium uppercase tracking-wide text-dark-500 px-2 pt-1">
              ↓ Próximos
            </p>
          )}
          {upcoming.map(({ item, time }, i) => {
            const rowKey = String(item.media_file_id || item.id || `u-${i}`);
            const movable = !!item.media_file_id;
            return (
              <div
                key={`u-${i}`}
                draggable={movable}
                onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', rowKey); } catch (err) {} setDragId(rowKey); }}
                onDragOver={(e) => { if (dragId && dragId !== rowKey) e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); handleReorderTo(dragId, i); setDragId(null); }}
                onDragEnd={() => setDragId(null)}
                className={`flex items-center gap-2 rounded px-2 py-1.5 ${
                  dragId === rowKey ? 'opacity-40 ring-1 ring-primary-600' : 'bg-dark-800/50'
                } ${movable ? 'cursor-grab active:cursor-grabbing hover:bg-dark-800' : ''}`}
              >
                <span className="text-[10px] text-dark-500 select-none" title={movable ? 'Arrastrá para reordenar' : undefined}>⠿</span>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-dark-200 truncate">{item.title || '—'}</p>
                  <p className="text-[10px] text-dark-500 truncate">
                    {item.artist || ''}{item.program ? ` · ${item.program}` : ''}
                    {item.folder_name ? ` · 📁 ${item.folder_name}` : ''}
                  </p>
                </div>
                <span className="text-[10px] font-mono text-dark-400">{fmtTime(time)}</span>
                <span className="text-[10px] text-dark-500">{fmtDur(item.duration_seconds)}</span>
                {movable && (
                  <>
                    <button
                      type="button"
                      onClick={() => send('move-to-front', { media_file_id: item.media_file_id })}
                      disabled={busy === `move-to-front${item.media_file_id}` || i === 0}
                      className="flex h-5 w-5 items-center justify-center rounded bg-primary-700 text-white hover:bg-primary-600 text-[10px] disabled:opacity-30"
                      title="Poner como primer próximo (suena apenas termine el tema actual)"
                    >
                      ⏫
                    </button>
                    <button
                      type="button"
                      onClick={() => moveBy(i, -1)}
                      disabled={i === 0}
                      className="flex h-5 w-5 items-center justify-center rounded bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white text-[10px] disabled:opacity-30"
                      title="Subir en la cola"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveBy(i, 1)}
                      disabled={i === upcoming.length - 1}
                      className="flex h-5 w-5 items-center justify-center rounded bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white text-[10px] disabled:opacity-30"
                      title="Bajar en la cola"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => send('play', { media_file_id: item.media_file_id })}
                      disabled={busy === `play${item.media_file_id}`}
                      className="flex h-5 w-5 items-center justify-center rounded bg-dark-700 text-dark-200 hover:bg-primary-600 hover:text-white text-[10px]"
                      title="Reproducir ahora (corta el actual)"
                    >
                      ▶
                    </button>
                    <button
                      type="button"
                      onClick={() => send('remove', { media_file_id: item.media_file_id })}
                      disabled={busy === `remove${item.media_file_id}`}
                      className="flex h-5 w-5 items-center justify-center rounded bg-dark-700 text-error-400 hover:bg-error-900/40 text-[10px]"
                      title="Quitar de la cola"
                    >
                      ✕
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-dark-500">
        <span className="flex items-center gap-1"><span className="text-dark-600">✔</span> Ya sonó</span>
        <span className="flex items-center gap-1"><span>🔴</span> Sonando ahora</span>
        <span className="flex items-center gap-1"><span className="text-dark-500">⠿</span> Arrastrá o usá ↑↓ para reordenar</span>
        <span className="flex items-center gap-1"><span className="text-primary-400">⏫</span> Primer próximo (no corta el tema actual)</span>
        <span className="ml-auto">Subí para ver el historial · Bajá para ver los próximos</span>
      </div>
    </div>
  );
}
